/**
 * Hook de Auto-logout por inatividade (tenant-wide, v3.16).
 *
 * Como usar:
 *   useAutoLogout();   // carrega `auto_logoff_minutos` da config e ativa
 *
 * Comportamento:
 *   - `auto_logoff_minutos = 0` → hook fica inerte (não instala listeners).
 *   - `auto_logoff_minutos >= 10` → sessão termina após N min sem
 *     mousemove / keydown / click / touchstart / scroll / wheel.
 *   - Ao expirar: limpa sessionStorage/localStorage do cliente e navega
 *     para `/cliente` (tela de login) com mensagem no toast.
 *   - Qualquer evento reseta o contador (throttle de 500ms para não sobrecarregar).
 */
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import clienteApi from "@/lib/clienteApi";
import { setAutoLogoutMinutes, markActivity } from "@/hooks/autoLogoutStore";

const EVENTS = ["mousemove", "mousedown", "keydown", "click", "touchstart", "scroll", "wheel"];

export default function useAutoLogout() {
  const navigate = useNavigate();
  const timerRef = useRef(null);
  const lastResetRef = useRef(0);
  const minutesRef = useRef(0);

  useEffect(() => {
    let cancelled = false;

    // 1) Buscar a config uma única vez ao montar
    (async () => {
      try {
        const res = await clienteApi.get("/cliente/configuracoes");
        if (cancelled) return;
        const m = Number(res?.data?.auto_logoff_minutos || 0);
        if (!Number.isFinite(m) || m < 10) return; // desligado
        minutesRef.current = m;
        setAutoLogoutMinutes(m);        // publica no store p/ badge de UI
        markActivity();                  // marca ativação como atividade inicial
        installListeners(m);
      } catch {
        // Silencioso — se falhar, apenas não ativa o auto-logout
      }
    })();

    function fireLogout() {
      // Limpa credenciais salvas
      try {
        sessionStorage.removeItem("brave_cliente_token");
        sessionStorage.removeItem("brave_cliente_data");
        localStorage.removeItem("brave_cliente_token");
        localStorage.removeItem("brave_cliente_data");
      } catch {}
      toast.info(`Sessão encerrada por ${minutesRef.current} minutos de inatividade.`);
      navigate("/cliente", { replace: true });
    }

    function resetTimer() {
      const now = Date.now();
      // Throttle: só reseta o timer se passou pelo menos 500ms desde o último reset
      if (now - lastResetRef.current < 500) return;
      lastResetRef.current = now;
      markActivity();                    // publica no store p/ badge recalcular
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(fireLogout, minutesRef.current * 60 * 1000);
    }

    function installListeners(m) {
      // Arma o primeiro timer
      timerRef.current = setTimeout(fireLogout, m * 60 * 1000);
      // Ouve eventos globais
      EVENTS.forEach((ev) => window.addEventListener(ev, resetTimer, { passive: true }));
    }

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      EVENTS.forEach((ev) => window.removeEventListener(ev, resetTimer));
      setAutoLogoutMinutes(0);           // desativa badge ao desmontar
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
